import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Download, 
  RefreshCw, 
  Users, 
  TrendingUp, 
  Calendar,
  Smartphone,
  Plus,
  Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { UserLayout } from '../components/User/UserLayout';

interface SpinLog {
  id: string;
  user_name: string;
  user_phone: string;
  user_email: string;
  prize_label: string;
  prize_code: string;
  device: string;
  mode: string;
  created_at: string;
}

interface Stats {
  totalSpins: number;
  todaySpins: number;
  uniqueUsers: number;
  prizeDistribution: Record<string, number>;
  deviceDistribution: Record<string, number>;
  modeDistribution: Record<string, number>;
  recentSpins: any[];
}

interface SpinCode {
  id: string;
  code: string;
  is_used: boolean;
  used_by_phone: string | null;
  used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export const SpinWheelAnalytics: React.FC = () => {
  const { pageId, componentId } = useParams<{ pageId: string; componentId: string }>();
  const { t } = useTranslation('analytics');

  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<SpinLog[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [codes, setCodes] = useState<SpinCode[]>([]);
  const [activeTab, setActiveTab] = useState<'logs' | 'stats' | 'codes'>('stats');
  
  // Code generator state
  const [showCodeGenerator, setShowCodeGenerator] = useState(false);
  const [codeCount, setCodeCount] = useState(10);
  const [codePrefix, setCodePrefix] = useState('SPIN');
  const [expiresInDays, setExpiresInDays] = useState(30);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch stats
      const statsRes = await fetch(
        `${API_URL}/spin/${pageId}/stats?componentId=${componentId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Fetch logs
      const logsRes = await fetch(
        `${API_URL}/spin/${pageId}/logs?componentId=${componentId}&limit=100`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setLogs(logsData.logs);
      }

      // Fetch codes
      const codesRes = await fetch(
        `${API_URL}/spin/${pageId}/codes?componentId=${componentId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (codesRes.ok) {
        const codesData = await codesRes.json();
        setCodes(codesData.codes);
      }

    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('spinWheel.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageId, componentId]);

  // Generate codes
  const handleGenerateCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/spin/${pageId}/generate-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          componentId,
          count: codeCount,
          prefix: codePrefix,
          expiresInDays
        })
      });

      if (!res.ok) throw new Error('Failed to generate codes');

      const data = await res.json();
      toast.success(t('spinWheel.codes.generated', { count: data.count }));
      setShowCodeGenerator(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const csv = [
      ['Name', 'Phone', 'Email', 'Prize', 'Code', 'Device', 'Mode', 'Date'],
      ...logs.map(log => [
        log.user_name || '',
        log.user_phone || '',
        log.user_email || '',
        log.prize_label,
        log.prize_code || '',
        log.device,
        log.mode,
        new Date(log.created_at).toLocaleString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spin-logs-${pageId}-${Date.now()}.csv`;
    a.click();
    toast.success(t('spinWheel.csvExported'));
  };

  // Delete code
  const handleDeleteCode = async (codeId: string) => {
    if (!confirm(t('spinWheel.codes.deleteConfirm'))) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/spin/${pageId}/codes/${codeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to delete code');

      toast.success(t('spinWheel.codes.deleted'));
      fetchData();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center h-screen">
          <RefreshCw className="animate-spin" size={32} />
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <span>🎰</span> {t('spinWheel.title')}
              </h1>
              <p className="text-sm text-gray-500">{t('spinWheel.componentIdLabel', { id: componentId?.substring(0, 8) })}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
              >
                <RefreshCw size={16} />
                {t('spinWheel.refresh')}
              </button>
              {activeTab === 'logs' && (
                <button
                  onClick={handleExportCSV}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
                >
                  <Download size={16} />
                  {t('spinWheel.exportCsv')}
                </button>
              )}
            </div>
          </div>
        </div>

      {/* Stats Cards */}
      {stats && (
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <TrendingUp className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('spinWheel.stats.totalSpins')}</p>
                  <p className="text-2xl font-bold">{stats.totalSpins}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Calendar className="text-green-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('spinWheel.stats.today')}</p>
                  <p className="text-2xl font-bold">{stats.todaySpins}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Users className="text-purple-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('spinWheel.stats.uniqueUsers')}</p>
                  <p className="text-2xl font-bold">{stats.uniqueUsers}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Smartphone className="text-yellow-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('spinWheel.stats.mobile')}</p>
                  <p className="text-2xl font-bold">
                    {stats.deviceDistribution.mobile || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'stats'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('spinWheel.tabs.statistics')}
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'logs'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('spinWheel.tabs.logs', { count: logs.length })}
          </button>
          <button
            onClick={() => setActiveTab('codes')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'codes'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('spinWheel.tabs.codes', { available: codes.filter(c => !c.is_used).length, total: codes.length })}
          </button>
        </div>
      </div>

      {/* Content */}
      <div>
        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Prize Distribution */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-bold mb-4">{t('spinWheel.prizeDistribution')}</h3>
              <div className="space-y-3">
                {Object.entries(stats.prizeDistribution).map(([prize, count]) => (
                  <div key={prize}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">{prize}</span>
                      <span className="text-sm text-gray-500">{count} ({((count / stats.totalSpins) * 100).toFixed(1)}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(count / stats.totalSpins) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Device Distribution */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-bold mb-4">{t('spinWheel.deviceDistribution')}</h3>
              <div className="space-y-3">
                {Object.entries(stats.deviceDistribution).map(([device, count]) => (
                  <div key={device}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium capitalize">{device}</span>
                      <span className="text-sm text-gray-500">{count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${(count / stats.totalSpins) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.logsTable.name')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.logsTable.phone')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.logsTable.prize')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.logsTable.code')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.logsTable.device')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.logsTable.date')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">{log.user_name || '-'}</td>
                      <td className="px-6 py-4 text-sm">{log.user_phone || '-'}</td>
                      <td className="px-6 py-4 text-sm font-medium text-blue-600">{log.prize_label}</td>
                      <td className="px-6 py-4 text-sm">
                        <code className="px-2 py-1 bg-gray-100 rounded">{log.prize_code || '-'}</code>
                      </td>
                      <td className="px-6 py-4 text-sm capitalize">{log.device}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Codes Tab */}
        {activeTab === 'codes' && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <h3 className="text-lg font-bold">{t('spinWheel.codes.heading')}</h3>
              <button
                onClick={() => setShowCodeGenerator(!showCodeGenerator)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
              >
                <Plus size={16} />
                {t('spinWheel.codes.generateButton')}
              </button>
            </div>

            {/* Code Generator */}
            {showCodeGenerator && (
              <div className="bg-white p-6 rounded-lg shadow mb-4">
                <h4 className="font-bold mb-4">{t('spinWheel.codes.generateHeading')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('spinWheel.codes.count')}</label>
                    <input
                      type="number"
                      value={codeCount}
                      onChange={(e) => setCodeCount(parseInt(e.target.value))}
                      min="1"
                      max="1000"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('spinWheel.codes.prefix')}</label>
                    <input
                      type="text"
                      value={codePrefix}
                      onChange={(e) => setCodePrefix(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('spinWheel.codes.expiresInDays')}</label>
                    <input
                      type="number"
                      value={expiresInDays}
                      onChange={(e) => setExpiresInDays(parseInt(e.target.value))}
                      min="1"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
                <button
                  onClick={handleGenerateCodes}
                  className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                >
                  {t('spinWheel.codes.generateNow', { count: codeCount })}
                </button>
              </div>
            )}

            {/* Codes Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.codesTable.code')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.codesTable.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.codesTable.usedBy')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.codesTable.expires')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('spinWheel.codesTable.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {codes.map((code) => (
                    <tr key={code.id} className={code.is_used ? 'bg-gray-50' : ''}>
                      <td className="px-6 py-4">
                        <code className="px-2 py-1 bg-gray-100 rounded font-mono">{code.code}</code>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${code.is_used ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {code.is_used ? t('spinWheel.codesTable.used') : t('spinWheel.codesTable.available')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">{code.used_by_phone || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : t('spinWheel.codesTable.never')}
                      </td>
                      <td className="px-6 py-4">
                        {!code.is_used && (
                          <button
                            onClick={() => handleDeleteCode(code.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      </div>
    </UserLayout>
  );
};

